'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

// ─── Types ────────────────────────────────────────────────────────────────────
type TabType = 'overview' | 'bookings' | 'earnings' | 'wallet' | 'reviews' | 'performance' | 'availability';

interface Booking {
  id: string;
  clientName: string;
  clientInitials: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  amount: number;
  status: 'upcoming' | 'completed' | 'cancelled' | 'pending';
  paymentStatus: 'paid' | 'pending' | 'refunded';
  notes?: string;
}

interface EarningEntry {
  id: string;
  clientName: string;
  service: string;
  date: string;
  gross: number;
  platformFee: number;
  net: number;
  status: 'paid' | 'pending' | 'processing';
}

interface WalletData {
  balance_usd: number;
  total_earned_usd: number;
  pending_usd: number;
}

interface WalletTransaction {
  id: string;
  amount_usd: number;
  net_usd: number;
  platform_fee_usd: number;
  transaction_type: string;
  status: string;
  description: string;
  created_at: string;
}

interface ReviewEntry {
  id: string;
  rating: number;
  feedback: string;
  reviewer_role: string;
  created_at: string;
}

interface AvailabilitySlot {
  id: string;
  day: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

// ─── Quick-Start Modal ─────────────────────────────────────────────────────────
interface QuickStartModalProps {
  bookings: Booking[];
  onClose: () => void;
  onStart: (booking: Booking | null) => void;
}

function QuickStartModal({ bookings, onClose, onStart }: QuickStartModalProps) {
  const upcomingBookings = bookings.filter(b => b.status === 'upcoming' || b.status === 'pending');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <Icon name="VideoCameraIcon" size={18} className="text-teal-600" />
            </div>
            <h2 className="font-700 text-foreground">Start Video Session</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {upcomingBookings.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground mb-3">Select a client to start a session with:</p>
              {upcomingBookings.slice(0, 5).map(booking => (
                <button
                  key={booking.id}
                  onClick={() => onStart(booking)}
                  className="w-full flex items-center gap-3 p-3 bg-muted/40 hover:bg-primary/5 border border-transparent hover:border-primary/20 rounded-xl transition-all text-left group"
                >
                  <div className="w-9 h-9 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-600 flex-shrink-0">
                    {booking.clientInitials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-600 text-foreground truncate">{booking.clientName}</p>
                    <p className="text-xs text-muted-foreground truncate">{booking.service} · {booking.date} {booking.time}</p>
                  </div>
                  <Icon name="ArrowRightIcon" size={16} className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                </button>
              ))}
            </>
          ) : (
            <div className="text-center py-6">
              <Icon name="CalendarDaysIcon" size={36} className="mx-auto mb-2 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No upcoming bookings found.</p>
              <p className="text-xs text-muted-foreground mt-1">You can still start an instant session below.</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 pt-2 border-t border-border flex flex-col gap-2">
          <button
            onClick={() => onStart(null)}
            className="w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-600 text-sm px-4 py-2.5 rounded-xl transition-colors"
          >
            <Icon name="VideoCameraIcon" size={16} />
            Start Instant Session
          </button>
          <button onClick={onClose} className="w-full text-sm text-muted-foreground hover:text-foreground py-2 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Active Session Banner ─────────────────────────────────────────────────────
interface ActiveSessionBannerProps {
  clientName: string;
  duration: string;
  onJoin: () => void;
  onEnd: () => void;
}

function ActiveSessionBanner({ clientName, duration, onJoin, onEnd }: ActiveSessionBannerProps) {
  return (
    <div className="bg-teal-600 text-white rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap shadow-lg">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Icon name="VideoCameraIcon" size={20} className="text-white" />
          </div>
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-400 border-2 border-teal-600 animate-pulse" />
        </div>
        <div className="min-w-0">
          <p className="font-700 text-sm">Active Session — {clientName}</p>
          <p className="text-xs text-teal-100">In progress · {duration}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onJoin}
          className="flex items-center gap-1.5 bg-white text-teal-700 font-600 text-xs px-4 py-2 rounded-xl hover:bg-teal-50 transition-colors"
        >
          <Icon name="ArrowRightCircleIcon" size={15} />
          Rejoin
        </button>
        <button
          onClick={onEnd}
          className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white font-600 text-xs px-4 py-2 rounded-xl transition-colors"
        >
          <Icon name="XMarkIcon" size={15} />
          End
        </button>
      </div>
    </div>
  );
}

// ─── Status Config ─────────────────────────────────────────────────────────────
const BOOKING_STATUS_CONFIG = {
  upcoming: { label: 'Upcoming', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-400' },
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ─── Sub-components ────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, accent }: { icon: string; label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accent ?? 'bg-primary/10'}`}>
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={22} className={accent ? 'text-white' : 'text-primary'} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground font-500 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-2xl font-700 text-foreground leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Avatar({ initials, size = 'md' }: { initials: string; size?: 'sm' | 'md' }) {
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  return (
    <div className={`${sz} rounded-full gradient-teal flex items-center justify-center text-white font-600 flex-shrink-0`}>
      {initials}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Icon
          key={i}
          name="StarIcon"
          size={14}
          className={i <= rating ? 'text-amber-400' : 'text-muted-foreground/30'}
        />
      ))}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function ConsultantDashboardPage() {
  const supabase = createClient();
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [bookingFilter, setBookingFilter] = useState<'all' | 'upcoming' | 'completed' | 'cancelled' | 'pending'>('all');
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [newSlot, setNewSlot] = useState({ day: 'Monday', startTime: '09:00', endTime: '17:00' });

  // Session state
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [activeSession, setActiveSession] = useState<{ clientName: string; startTime: Date; bookingId?: string } | null>(null);
  const [sessionDuration, setSessionDuration] = useState('0:00');

  // Data state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [earnings, setEarnings] = useState<EarningEntry[]>([]);
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
  const [reviews, setReviews] = useState<ReviewEntry[]>([]);
  const [consultantProfileId, setConsultantProfileId] = useState<string | null>(null);
  const [consultantId, setConsultantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLive, setIsLive] = useState(false);

  // ─── Session timer ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeSession) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - activeSession.startTime.getTime()) / 1000);
      const mins = Math.floor(elapsed / 60);
      const secs = elapsed % 60;
      setSessionDuration(`${mins}:${secs.toString().padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeSession]);

  function handleQuickStart(booking: Booking | null) {
    setShowQuickStart(false);
    const clientName = booking?.clientName ?? 'Client';
    setActiveSession({ clientName, startTime: new Date(), bookingId: booking?.id });
    router.push('/video-session');
  }

  function handleEndSession() {
    setActiveSession(null);
    setSessionDuration('0:00');
  }

  function handleRejoinSession() {
    router.push('/video-session');
  }

  useEffect(() => {
    async function fetchDashboardData() {
      setLoading(true);
      setError(null);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        // Fetch consultant profile
        const { data: profile } = await supabase
          .from('consultant_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profile) setConsultantProfileId(profile.id);

        // Fetch bookings where user is provider
        const { data: bookingData, error: bookingError } = await supabase
          .from('consultation_bookings')
          .select(`id, service_title, client_name, booking_date, start_time, duration_minutes, status, payment_status, amount_usd, notes, consultant_id`)
          .eq('provider_user_id', user.id)
          .order('booking_date', { ascending: false });

        if (bookingError) { setError(bookingError.message); setLoading(false); return; }

        const mappedBookings: Booking[] = (bookingData ?? []).map((row) => {
          const clientName = row.client_name ?? 'Unknown Client';
          const nameWords = clientName.split(' ');
          const initials = nameWords.slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase();
          const statusMap: Record<string, Booking['status']> = { pending: 'pending', confirmed: 'upcoming', completed: 'completed', cancelled: 'cancelled' };
          const paymentStatusMap: Record<string, Booking['paymentStatus']> = { paid: 'paid', unpaid: 'pending', refunded: 'refunded' };
          const startTime = row.start_time ? row.start_time.slice(0, 5) : '';
          const [h] = startTime.split(':').map(Number);
          const period = h < 12 ? 'AM' : 'PM';
          const mins = row.duration_minutes ?? 60;
          return {
            id: row.id,
            clientName,
            clientInitials: initials,
            service: row.service_title ?? '',
            date: row.booking_date ?? '',
            time: startTime ? `${startTime} ${period}` : '',
            duration: `${mins} min`,
            amount: parseFloat(row.amount_usd ?? 0),
            status: statusMap[row.status] ?? 'pending',
            paymentStatus: paymentStatusMap[row.payment_status ?? 'unpaid'] ?? 'pending',
            notes: row.notes ?? undefined,
          };
        });
        setBookings(mappedBookings);

        // Detect consultant_id from first booking or profile id
        const firstConsultantId = bookingData?.[0]?.consultant_id ?? (profile?.id ?? null);
        setConsultantId(firstConsultantId);

        // Build earnings from completed/paid bookings
        const earningEntries: EarningEntry[] = mappedBookings
          .filter(b => b.status === 'completed' || b.paymentStatus === 'paid')
          .map(b => {
            const gross = b.amount;
            const platformFee = parseFloat((gross * 0.1).toFixed(2));
            const net = parseFloat((gross - platformFee).toFixed(2));
            return { id: b.id, clientName: b.clientName, service: b.service, date: b.date, gross, platformFee, net, status: b.paymentStatus === 'paid' ? 'paid' : 'pending' };
          });
        setEarnings(earningEntries);

        // Fetch wallet data
        if (firstConsultantId) {
          const { data: walletData } = await supabase
            .from('consultant_wallets')
            .select('balance_usd, total_earned_usd, pending_usd')
            .eq('consultant_id', String(firstConsultantId))
            .maybeSingle();
          if (walletData) setWallet(walletData);

          const { data: txData } = await supabase
            .from('consultant_wallet_transactions')
            .select('id, amount_usd, net_usd, platform_fee_usd, transaction_type, status, description, created_at')
            .eq('consultant_id', String(firstConsultantId))
            .order('created_at', { ascending: false })
            .limit(20);
          setWalletTransactions(txData ?? []);
        }

        // Fetch reviews where current user is reviewee
        const { data: reviewData } = await supabase
          .from('session_reviews')
          .select('id, rating, feedback, reviewer_role, created_at')
          .eq('reviewee_id', user.id)
          .order('created_at', { ascending: false });
        setReviews(reviewData ?? []);

        // Fetch availability slots
        if (profile) {
          const { data: slotData } = await supabase
            .from('consultant_availability_slots')
            .select('id, day_of_week, start_time, end_time, is_active')
            .eq('consultant_profile_id', profile.id)
            .order('day_of_week');
          setSlots((slotData ?? []).map(s => ({
            id: s.id,
            day: s.day_of_week.charAt(0).toUpperCase() + s.day_of_week.slice(1),
            startTime: s.start_time?.slice(0, 5) ?? '',
            endTime: s.end_time?.slice(0, 5) ?? '',
            isActive: s.is_active ?? true,
          })));
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }
    fetchDashboardData();
  }, []);

  // ─── Real-time subscriptions ─────────────────────────────────────────────────
  useEffect(() => {
    if (!consultantId) return;

    const walletChannel = supabase
      .channel(`consultant_wallet_${consultantId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'consultant_wallets',
          filter: `consultant_id=eq.${consultantId}`,
        },
        (payload) => {
          const updated = payload.new as WalletData;
          setWallet({
            balance_usd: updated.balance_usd,
            total_earned_usd: updated.total_earned_usd,
            pending_usd: updated.pending_usd,
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setIsLive(true);
      });

    const txChannel = supabase
      .channel(`consultant_wallet_tx_${consultantId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'consultant_wallet_transactions',
          filter: `consultant_id=eq.${consultantId}`,
        },
        (payload) => {
          const newTx = payload.new as WalletTransaction;
          setWalletTransactions((prev) => [newTx, ...prev].slice(0, 20));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'consultant_wallet_transactions',
          filter: `consultant_id=eq.${consultantId}`,
        },
        (payload) => {
          const updatedTx = payload.new as WalletTransaction;
          setWalletTransactions((prev) =>
            prev.map((tx) => (tx.id === updatedTx.id ? updatedTx : tx))
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'consultant_wallet_transactions',
          filter: `consultant_id=eq.${consultantId}`,
        },
        (payload) => {
          const deletedId = (payload.old as { id: string }).id;
          setWalletTransactions((prev) => prev.filter((tx) => tx.id !== deletedId));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(walletChannel);
      supabase.removeChannel(txChannel);
      setIsLive(false);
    };
  }, [consultantId]);

  // ─── Computed stats ──────────────────────────────────────────────────────────
  const totalEarnings = earnings.filter(e => e.status === 'paid').reduce((s, e) => s + e.net, 0);
  const pendingEarnings = earnings.filter(e => e.status === 'pending').reduce((s, e) => s + e.net, 0);
  const upcomingCount = bookings.filter(b => b.status === 'upcoming').length;
  const completedCount = bookings.filter(b => b.status === 'completed').length;
  const cancelledCount = bookings.filter(b => b.status === 'cancelled').length;
  const totalBookings = bookings.length;
  const cancellationRate = totalBookings > 0 ? Math.round((cancelledCount / totalBookings) * 100) : 0;
  const completionRate = totalBookings > 0 ? Math.round((completedCount / totalBookings) * 100) : 0;
  const activeSlots = slots.filter(s => s.isActive).length;
  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '—';
  const fiveStarCount = reviews.filter(r => r.rating === 5).length;

  const filteredBookings = bookingFilter === 'all' ? bookings : bookings.filter(b => b.status === bookingFilter);

  async function toggleSlot(id: string) {
    const slot = slots.find(s => s.id === id);
    if (!slot) return;
    const newActive = !slot.isActive;
    const { error: updateError } = await supabase.from('consultant_availability_slots').update({ is_active: newActive }).eq('id', id);
    if (!updateError) setSlots(prev => prev.map(s => s.id === id ? { ...s, isActive: newActive } : s));
  }

  async function removeSlot(id: string) {
    const { error: deleteError } = await supabase.from('consultant_availability_slots').delete().eq('id', id);
    if (!deleteError) setSlots(prev => prev.filter(s => s.id !== id));
  }

  async function addSlot() {
    if (!consultantProfileId) return;
    const { data, error: insertError } = await supabase
      .from('consultant_availability_slots')
      .insert({ consultant_profile_id: consultantProfileId, day_of_week: newSlot.day.toLowerCase(), start_time: newSlot.startTime, end_time: newSlot.endTime, is_active: true })
      .select().single();
    if (!insertError && data) {
      setSlots(prev => [...prev, { id: data.id, day: newSlot.day, startTime: newSlot.startTime, endTime: newSlot.endTime, isActive: true }]);
    }
    setShowAddSlot(false);
    setNewSlot({ day: 'Monday', startTime: '09:00', endTime: '17:00' });
  }

  const tabs: { id: TabType; label: string; icon: string; badge?: number }[] = [
    { id: 'overview', label: 'Overview', icon: 'Squares2X2Icon' },
    { id: 'bookings', label: 'Bookings', icon: 'CalendarDaysIcon', badge: upcomingCount },
    { id: 'earnings', label: 'Earnings', icon: 'BanknotesIcon' },
    { id: 'wallet', label: 'Wallet', icon: 'WalletIcon' },
    { id: 'reviews', label: 'Reviews', icon: 'StarIcon', badge: reviews.length },
    { id: 'performance', label: 'Performance', icon: 'ChartBarIcon' },
    { id: 'availability', label: 'Availability', icon: 'ClockIcon' },
  ];

  return (
    <AppLayout activeRoute="/consultant-dashboard">
      <div className="min-h-screen bg-background">
        {/* Quick-Start Modal */}
        {showQuickStart && (
          <QuickStartModal
            bookings={bookings}
            onClose={() => setShowQuickStart(false)}
            onStart={handleQuickStart}
          />
        )}

        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-5">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-700 text-foreground">Consultant Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Bookings, earnings, wallet, reviews & performance</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isLive && (
                <span className="flex items-center gap-1.5 text-xs font-500 text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block animate-pulse" />
                  Live
                </span>
              )}
              {activeSession ? (
                <span className="flex items-center gap-1.5 text-xs font-500 text-teal-700 bg-teal-50 border border-teal-200 px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block animate-pulse" />
                  Session Active · {sessionDuration}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs font-500 text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  Active on Platform
                </span>
              )}
              <button
                onClick={() => setShowQuickStart(true)}
                className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-600 text-sm px-4 py-2 rounded-xl transition-colors shadow-sm"
              >
                <Icon name="VideoCameraIcon" size={16} />
                Quick Start
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Active Session Banner */}
          {activeSession && (
            <div className="mb-6">
              <ActiveSessionBanner
                clientName={activeSession.clientName}
                duration={sessionDuration}
                onJoin={handleRejoinSession}
                onEnd={handleEndSession}
              />
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
              <p className="text-sm text-muted-foreground">Loading dashboard…</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center mb-6">
              <Icon name="ExclamationCircleIcon" size={28} className="text-red-500 mx-auto mb-2" />
              <p className="text-sm font-600 text-red-700">{error}</p>
            </div>
          )}

          {!loading && (
            <>
              {/* Tabs */}
              <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-2xl p-1 mb-6 overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-500 transition-all whitespace-nowrap relative ${
                      activeTab === tab.id ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                    {tab.label}
                    {tab.badge && tab.badge > 0 ? (
                      <span className="bg-primary text-white text-xs font-600 px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none">
                        {tab.badge}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>

              {/* ── OVERVIEW ── */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon="BanknotesIcon" label="Total Earned" value={`$${totalEarnings.toLocaleString()}`} sub="Net after platform fee" />
                    <StatCard icon="WalletIcon" label="Wallet Balance" value={wallet ? `$${wallet.balance_usd.toFixed(2)}` : `$${pendingEarnings.toFixed(0)}`} sub="Available to withdraw" />
                    <StatCard icon="CalendarDaysIcon" label="Upcoming Sessions" value={String(upcomingCount)} sub="Confirmed bookings" />
                    <StatCard icon="StarIcon" label="Avg Rating" value={String(avgRating)} sub={`${reviews.length} reviews`} />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    <div className="lg:col-span-3 bg-card border border-border rounded-2xl p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="font-600 text-foreground">Upcoming Sessions</h2>
                        <button onClick={() => setActiveTab('bookings')} className="text-xs text-primary font-500 hover:underline">View all</button>
                      </div>
                      <div className="space-y-3">
                        {bookings.filter(b => b.status === 'upcoming' || b.status === 'pending').slice(0, 3).map(booking => (
                          <div key={booking.id} className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                            <Avatar initials={booking.clientInitials} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-600 text-foreground truncate">{booking.clientName}</p>
                              <p className="text-xs text-muted-foreground truncate">{booking.service}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs font-600 text-foreground">{booking.date}</p>
                              <p className="text-xs text-muted-foreground">{booking.time} · {booking.duration}</p>
                            </div>
                            <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${BOOKING_STATUS_CONFIG[booking.status].bg} ${BOOKING_STATUS_CONFIG[booking.status].text}`}>
                              {BOOKING_STATUS_CONFIG[booking.status].label}
                            </span>
                          </div>
                        ))}
                        {bookings.filter(b => b.status === 'upcoming' || b.status === 'pending').length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-6">No upcoming sessions</p>
                        )}
                      </div>
                    </div>

                    <div className="lg:col-span-2 space-y-4">
                      {/* Wallet snapshot */}
                      <div className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <h2 className="font-600 text-foreground">Wallet</h2>
                          <button onClick={() => setActiveTab('wallet')} className="text-xs text-primary font-500 hover:underline">Details</button>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center p-2.5 bg-emerald-50 rounded-xl">
                            <span className="text-xs text-emerald-700 font-500">Available</span>
                            <span className="text-sm font-700 text-emerald-700">${wallet?.balance_usd?.toFixed(2) ?? '0.00'}</span>
                          </div>
                          <div className="flex justify-between items-center p-2.5 bg-amber-50 rounded-xl">
                            <span className="text-xs text-amber-700 font-500">Pending</span>
                            <span className="text-sm font-700 text-amber-700">${wallet?.pending_usd?.toFixed(2) ?? pendingEarnings.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center p-2.5 bg-blue-50 rounded-xl">
                            <span className="text-xs text-blue-700 font-500">Total Earned</span>
                            <span className="text-sm font-700 text-blue-700">${wallet?.total_earned_usd?.toFixed(2) ?? totalEarnings.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Performance snapshot */}
                      <div className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <h2 className="font-600 text-foreground">Performance</h2>
                          <button onClick={() => setActiveTab('performance')} className="text-xs text-primary font-500 hover:underline">Full report</button>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground">Completion Rate</span>
                            <span className="text-sm font-600 text-emerald-700">{completionRate}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${completionRate}%` }} />
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-muted-foreground">Cancellation Rate</span>
                            <span className={`text-sm font-600 ${cancellationRate > 20 ? 'text-red-600' : 'text-foreground'}`}>{cancellationRate}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full transition-all ${cancellationRate > 20 ? 'bg-red-400' : 'bg-amber-400'}`} style={{ width: `${cancellationRate}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground bg-muted/40 rounded-xl px-4 py-3 flex items-center gap-2">
                    <Icon name="InformationCircleIcon" size={14} className="flex-shrink-0" />
                    Platform fee is 10% per session. Payouts are processed within 3–5 business days after session completion.
                  </div>
                </div>
              )}

              {/* ── BOOKINGS ── */}
              {activeTab === 'bookings' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {(['all', 'upcoming', 'completed', 'cancelled', 'pending'] as const).map(f => (
                      <button
                        key={f}
                        onClick={() => setBookingFilter(f)}
                        className={`px-4 py-2 rounded-xl text-sm font-500 transition-colors capitalize ${
                          bookingFilter === f ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {f === 'all' ? 'All Bookings' : f}
                        {f === 'cancelled' && cancelledCount > 0 && (
                          <span className="ml-1.5 bg-red-100 text-red-600 text-xs font-600 px-1.5 py-0.5 rounded-full">{cancelledCount}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-3">
                    {filteredBookings.map(booking => (
                      <div key={booking.id} className="bg-card border border-border rounded-2xl p-5">
                        <div className="flex items-start gap-4 flex-wrap">
                          <Avatar initials={booking.clientInitials} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-600 text-foreground">{booking.clientName}</h3>
                              <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${BOOKING_STATUS_CONFIG[booking.status].bg} ${BOOKING_STATUS_CONFIG[booking.status].text}`}>
                                {BOOKING_STATUS_CONFIG[booking.status].label}
                              </span>
                              <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${booking.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-700' : booking.paymentStatus === 'refunded' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>
                                {booking.paymentStatus === 'paid' ? '✓ Paid' : booking.paymentStatus === 'refunded' ? '↩ Refunded' : 'Payment pending'}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{booking.service}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1"><Icon name="CalendarDaysIcon" size={13} />{booking.date}</span>
                              <span className="flex items-center gap-1"><Icon name="ClockIcon" size={13} />{booking.time} · {booking.duration}</span>
                              <span className="flex items-center gap-1 font-600 text-foreground"><Icon name="BanknotesIcon" size={13} />${booking.amount}</span>
                            </div>
                            {booking.notes && (
                              <p className="text-xs text-muted-foreground mt-2 bg-muted/40 rounded-lg px-3 py-2 italic">&ldquo;{booking.notes}&rdquo;</p>
                            )}
                          </div>
                          {booking.status === 'upcoming' && (
                            <button className="text-xs font-500 text-primary border border-primary/30 bg-primary/5 px-3 py-1.5 rounded-lg hover:bg-primary/10 transition-colors flex-shrink-0">
                              Join Call
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {filteredBookings.length === 0 && (
                      <div className="text-center py-16 text-muted-foreground">
                        <Icon name="CalendarDaysIcon" size={40} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No bookings found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── EARNINGS ── */}
              {activeTab === 'earnings' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon="BanknotesIcon" label="Net Earned" value={`$${totalEarnings.toLocaleString()}`} sub="After platform fee" />
                    <StatCard icon="ArrowPathIcon" label="Pending" value={`$${pendingEarnings.toFixed(0)}`} sub="In processing" />
                    <StatCard icon="ReceiptPercentIcon" label="Platform Fee" value="10%" sub="Per session" />
                    <StatCard icon="CheckCircleIcon" label="Sessions Paid" value={String(earnings.filter(e => e.status === 'paid').length)} sub="This month" />
                  </div>

                  <div className="bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border">
                      <h2 className="font-600 text-foreground">Transaction History</h2>
                    </div>
                    {earnings.length === 0 ? (
                      <div className="text-center py-16 text-muted-foreground">
                        <Icon name="BanknotesIcon" size={40} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No earnings yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/30">
                              <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Client</th>
                              <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Service</th>
                              <th className="text-left px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Date</th>
                              <th className="text-right px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Gross</th>
                              <th className="text-right px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Fee (10%)</th>
                              <th className="text-right px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Net</th>
                              <th className="text-center px-5 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {earnings.map(entry => (
                              <tr key={entry.id} className="hover:bg-muted/20 transition-colors">
                                <td className="px-5 py-3.5 font-500 text-foreground">{entry.clientName}</td>
                                <td className="px-5 py-3.5 text-muted-foreground">{entry.service}</td>
                                <td className="px-5 py-3.5 text-muted-foreground">{entry.date}</td>
                                <td className="px-5 py-3.5 text-right text-foreground">${entry.gross}</td>
                                <td className="px-5 py-3.5 text-right text-red-500">−${entry.platformFee}</td>
                                <td className="px-5 py-3.5 text-right font-600 text-emerald-700">${entry.net}</td>
                                <td className="px-5 py-3.5 text-center">
                                  <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${entry.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                    {entry.status === 'paid' ? 'Paid' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-border bg-muted/30">
                              <td colSpan={5} className="px-5 py-3.5 font-600 text-foreground">Total Net Earnings</td>
                              <td className="px-5 py-3.5 text-right font-700 text-emerald-700 text-base">${earnings.reduce((s, e) => s + e.net, 0).toFixed(2)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── WALLET ── */}
              {activeTab === 'wallet' && (
                <div className="space-y-5">
                  {/* Balance cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-6 text-white">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="WalletIcon" size={20} className="text-white/80" />
                        <span className="text-sm font-500 text-white/80">Available Balance</span>
                      </div>
                      <p className="text-3xl font-700">${wallet?.balance_usd?.toFixed(2) ?? '0.00'}</p>
                      <p className="text-xs text-white/70 mt-1">Ready to withdraw</p>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="ArrowPathIcon" size={20} className="text-amber-500" />
                        <span className="text-sm font-500 text-muted-foreground">Pending</span>
                      </div>
                      <p className="text-3xl font-700 text-foreground">${wallet?.pending_usd?.toFixed(2) ?? pendingEarnings.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Processing (3–5 days)</p>
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="ChartBarIcon" size={20} className="text-blue-500" />
                        <span className="text-sm font-500 text-muted-foreground">Total Earned</span>
                      </div>
                      <p className="text-3xl font-700 text-foreground">${wallet?.total_earned_usd?.toFixed(2) ?? totalEarnings.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-1">All time</p>
                    </div>
                  </div>

                  {/* Wallet transactions */}
                  <div className="bg-card border border-border rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                      <h2 className="font-600 text-foreground">Wallet Transactions</h2>
                      <span className="text-xs text-muted-foreground">{walletTransactions.length} records</span>
                    </div>
                    {walletTransactions.length === 0 ? (
                      <div className="text-center py-16 text-muted-foreground">
                        <Icon name="WalletIcon" size={40} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No wallet transactions yet</p>
                        <p className="text-xs mt-1">Transactions appear after completed paid sessions</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {walletTransactions.map(tx => (
                          <div key={tx.id} className="px-5 py-4 flex items-center gap-4 hover:bg-muted/20 transition-colors">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tx.transaction_type === 'booking_payment' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <Icon
                                name={tx.transaction_type === 'booking_payment' ? 'ArrowDownIcon' : 'ArrowUpIcon'}
                                size={16}
                                className={tx.transaction_type === 'booking_payment' ? 'text-emerald-600' : 'text-red-500'}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-500 text-foreground capitalize">{tx.transaction_type.replace(/_/g, ' ')}</p>
                              {tx.description && <p className="text-xs text-muted-foreground truncate">{tx.description}</p>}
                              <p className="text-xs text-muted-foreground">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className={`text-sm font-700 ${tx.transaction_type === 'booking_payment' ? 'text-emerald-700' : 'text-red-600'}`}>
                                {tx.transaction_type === 'booking_payment' ? '+' : '−'}${tx.net_usd?.toFixed(2) ?? tx.amount_usd?.toFixed(2)}
                              </p>
                              {tx.platform_fee_usd > 0 && (
                                <p className="text-xs text-muted-foreground">Fee: ${tx.platform_fee_usd?.toFixed(2)}</p>
                              )}
                              <span className={`text-xs font-500 px-1.5 py-0.5 rounded-full ${tx.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                {tx.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
                    <Icon name="InformationCircleIcon" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">To withdraw funds, visit the <button onClick={() => window.location.href = '/consultant-withdrawals'} className="font-600 underline">Wallet &amp; Withdrawals</button> page to set up your bank account and request a payout.</p>
                  </div>
                </div>
              )}

              {/* ── REVIEWS ── */}
              {activeTab === 'reviews' && (
                <div className="space-y-5">
                  {/* Review summary */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon="StarIcon" label="Avg Rating" value={String(avgRating)} sub={`${reviews.length} total reviews`} />
                    <StatCard icon="HandThumbUpIcon" label="5-Star Reviews" value={String(fiveStarCount)} sub="Excellent ratings" />
                    <StatCard icon="ChatBubbleLeftEllipsisIcon" label="With Feedback" value={String(reviews.filter(r => r.feedback).length)} sub="Written reviews" />
                    <StatCard icon="UserGroupIcon" label="Client Reviews" value={String(reviews.filter(r => r.reviewer_role === 'client').length)} sub="From clients" />
                  </div>

                  {/* Rating distribution */}
                  {reviews.length > 0 && (
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h2 className="font-600 text-foreground mb-4">Rating Distribution</h2>
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map(star => {
                          const count = reviews.filter(r => r.rating === star).length;
                          const pct = reviews.length > 0 ? Math.round((count / reviews.length) * 100) : 0;
                          return (
                            <div key={star} className="flex items-center gap-3">
                              <div className="flex items-center gap-1 w-16 flex-shrink-0">
                                <span className="text-sm font-500 text-foreground">{star}</span>
                                <Icon name="StarIcon" size={13} className="text-amber-400" />
                              </div>
                              <div className="flex-1 bg-muted rounded-full h-2">
                                <div className="bg-amber-400 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs text-muted-foreground w-10 text-right flex-shrink-0">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Review list */}
                  <div className="space-y-3">
                    {reviews.length === 0 ? (
                      <div className="text-center py-16 text-muted-foreground bg-card border border-border rounded-2xl">
                        <Icon name="StarIcon" size={40} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No reviews yet</p>
                        <p className="text-xs mt-1">Reviews appear after clients complete sessions with you</p>
                      </div>
                    ) : (
                      reviews.map(review => (
                        <div key={review.id} className="bg-card border border-border rounded-2xl p-5">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                              <StarRating rating={review.rating} />
                              <span className="text-sm font-600 text-foreground">{review.rating}/5</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${review.reviewer_role === 'client' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                                {review.reviewer_role === 'client' ? 'Client Review' : 'Peer Review'}
                              </span>
                              <span className="text-xs text-muted-foreground">{review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}</span>
                            </div>
                          </div>
                          {review.feedback && (
                            <p className="text-sm text-muted-foreground italic leading-relaxed">&ldquo;{review.feedback}&rdquo;</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* ── PERFORMANCE ── */}
              {activeTab === 'performance' && (
                <div className="space-y-5">
                  {/* Key metrics */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon="CheckCircleIcon" label="Completion Rate" value={`${completionRate}%`} sub={`${completedCount} completed`} />
                    <StatCard icon="XCircleIcon" label="Cancellation Rate" value={`${cancellationRate}%`} sub={`${cancelledCount} cancelled`} />
                    <StatCard icon="CalendarDaysIcon" label="Total Bookings" value={String(totalBookings)} sub="All time" />
                    <StatCard icon="StarIcon" label="Avg Rating" value={String(avgRating)} sub={`${fiveStarCount} five-star`} />
                  </div>

                  {/* Booking breakdown */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h2 className="font-600 text-foreground mb-4">Booking Status Breakdown</h2>
                      <div className="space-y-3">
                        {[
                          { label: 'Completed', count: completedCount, color: 'bg-emerald-500', textColor: 'text-emerald-700' },
                          { label: 'Upcoming', count: upcomingCount, color: 'bg-blue-500', textColor: 'text-blue-700' },
                          { label: 'Pending', count: bookings.filter(b => b.status === 'pending').length, color: 'bg-amber-400', textColor: 'text-amber-700' },
                          { label: 'Cancelled', count: cancelledCount, color: 'bg-red-400', textColor: 'text-red-600' },
                        ].map(item => {
                          const pct = totalBookings > 0 ? Math.round((item.count / totalBookings) * 100) : 0;
                          return (
                            <div key={item.label}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-500 text-foreground">{item.label}</span>
                                <span className={`text-sm font-600 ${item.textColor}`}>{item.count} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div className={`${item.color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        {totalBookings === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-4">No booking data yet</p>
                        )}
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h2 className="font-600 text-foreground mb-4">Cancellations Detail</h2>
                      {cancelledCount === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Icon name="CheckCircleIcon" size={36} className="mx-auto mb-2 text-emerald-400" />
                          <p className="text-sm font-500 text-emerald-700">No cancellations</p>
                          <p className="text-xs mt-1">Keep up the great work!</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {bookings.filter(b => b.status === 'cancelled').map(b => (
                            <div key={b.id} className="flex items-center gap-3 p-3 bg-red-50/50 border border-red-100 rounded-xl">
                              <Avatar initials={b.clientInitials} size="sm" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-500 text-foreground truncate">{b.clientName}</p>
                                <p className="text-xs text-muted-foreground truncate">{b.service}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs text-muted-foreground">{b.date}</p>
                                <p className="text-xs font-500 text-red-600">${b.amount}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-4 pt-4 border-t border-border">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Cancellation impact</span>
                          <span className="font-600 text-red-600">
                            −${bookings.filter(b => b.status === 'cancelled').reduce((s, b) => s + b.amount, 0).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Estimated lost revenue from cancelled sessions</p>
                      </div>
                    </div>
                  </div>

                  {/* Payment status breakdown */}
                  <div className="bg-card border border-border rounded-2xl p-5">
                    <h2 className="font-600 text-foreground mb-4">Payment Performance</h2>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: 'Paid', count: bookings.filter(b => b.paymentStatus === 'paid').length, bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'CheckCircleIcon' },
                        { label: 'Pending', count: bookings.filter(b => b.paymentStatus === 'pending').length, bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ClockIcon' },
                        { label: 'Refunded', count: bookings.filter(b => b.paymentStatus === 'refunded').length, bg: 'bg-red-50', text: 'text-red-600', icon: 'ArrowUturnLeftIcon' },
                      ].map(item => (
                        <div key={item.label} className={`${item.bg} rounded-xl p-4 text-center`}>
                          <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className={`${item.text} mx-auto mb-1`} />
                          <p className={`text-xl font-700 ${item.text}`}>{item.count}</p>
                          <p className={`text-xs font-500 ${item.text}`}>{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── AVAILABILITY ── */}
              {activeTab === 'availability' && (
                <div className="space-y-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h2 className="font-600 text-foreground">Availability Slots</h2>
                      <p className="text-sm text-muted-foreground mt-0.5">{activeSlots} active slots · Clients can book during these windows</p>
                    </div>
                    {consultantProfileId && (
                      <button
                        onClick={() => setShowAddSlot(true)}
                        className="flex items-center gap-2 text-sm font-500 text-white bg-primary px-4 py-2.5 rounded-xl hover:bg-primary/90 transition-colors"
                      >
                        <Icon name="PlusIcon" size={16} />
                        Add Slot
                      </button>
                    )}
                  </div>

                  {!consultantProfileId && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
                      <Icon name="InformationCircleIcon" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-700">Complete your consultant onboarding to manage availability slots.</p>
                    </div>
                  )}

                  {showAddSlot && (
                    <div className="bg-card border border-primary/30 rounded-2xl p-5">
                      <h3 className="font-600 text-foreground mb-4">New Availability Slot</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Day of Week</label>
                          <select
                            value={newSlot.day}
                            onChange={e => setNewSlot(p => ({ ...p, day: e.target.value }))}
                            className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                          >
                            {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">Start Time</label>
                          <input type="time" value={newSlot.startTime} onChange={e => setNewSlot(p => ({ ...p, startTime: e.target.value }))} className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div>
                          <label className="block text-xs font-500 text-muted-foreground mb-1.5">End Time</label>
                          <input type="time" value={newSlot.endTime} onChange={e => setNewSlot(p => ({ ...p, endTime: e.target.value }))} className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={addSlot} className="text-sm font-500 text-white bg-primary px-4 py-2 rounded-xl hover:bg-primary/90 transition-colors">Save Slot</button>
                        <button onClick={() => setShowAddSlot(false)} className="text-sm font-500 text-muted-foreground bg-card border border-border px-4 py-2 rounded-xl hover:bg-muted transition-colors">Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {DAYS_OF_WEEK.map(day => {
                      const daySlots = slots.filter(s => s.day.toLowerCase() === day.toLowerCase());
                      if (daySlots.length === 0) return null;
                      return (
                        <div key={day} className="bg-card border border-border rounded-2xl p-5">
                          <h3 className="font-600 text-foreground mb-3">{day}</h3>
                          <div className="space-y-2">
                            {daySlots.map(slot => (
                              <div key={slot.id} className="flex items-center gap-3 p-3 bg-muted/40 rounded-xl">
                                <button
                                  onClick={() => toggleSlot(slot.id)}
                                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${slot.isActive ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                                >
                                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${slot.isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                </button>
                                <Icon name="ClockIcon" size={15} className="text-muted-foreground flex-shrink-0" />
                                <span className={`text-sm font-500 flex-1 ${slot.isActive ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                                  {slot.startTime} – {slot.endTime}
                                </span>
                                <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${slot.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                                  {slot.isActive ? 'Active' : 'Inactive'}
                                </span>
                                <button onClick={() => removeSlot(slot.id)} className="text-muted-foreground hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50">
                                  <Icon name="TrashIcon" size={15} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {slots.length === 0 && (
                      <div className="text-center py-16 text-muted-foreground">
                        <Icon name="ClockIcon" size={40} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No availability slots configured</p>
                        {consultantProfileId && (
                          <button onClick={() => setShowAddSlot(true)} className="mt-3 text-sm text-primary font-500 hover:underline">Add your first slot</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
